import { useEffect, useState } from "react";
import reqServiceMultTenant from "../../services/reqMultiTenant/reqStatusMultTenant";
import HeaderReport from "../../components/header/report";
import { BtnBack } from "../../components/button/button-back";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/loading";
import reqServiceEmail from "../../services/reqEMail/reqStatusEmail";
import reqServiceAssas from "../../services/reqAsaas/reqStatusAssas";
import reqServiceBilling from "../../services/reqBilling/reqStatusBilling";
import reqServiceWhatsApp from "../../services/reqWhatsApp/reqstatusWhatsapp";
import reqServiceAi from "../../services/ai/reqStatusAi";
import reqServiceWebhook from "../../services/reqStatusWebhook";
import LibDateTime from "../../util/libDateTime";
import ModalMain from "../../components/modal";
import { handleOpenModal, handleCloseModal } from "../../util/libModal";
import reqControlServer from "../../services/reqControlServer";

const IDENT_MODAL_ACAO = "modal-acao-servidor";

// PID, contagem de processos e data do binário são fatos do sistema
// operacional: o navegador não tem como lê-los. Só o servidor de deploy
// (3006), que roda na mesma máquina, consegue - chamando `paserver.sh status`.
// Enquanto essa rota não existe, `statusServidor` fica nulo e nada disso
// aparece na tela.

function formatarTamanho(bytes) {
	if (!bytes) return "-";
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatarData(epoch) {
	if (!epoch) return "-";
	return LibDateTime.formatDateToSaoPaulo(new Date(epoch * 1000), "dd/MM/yyyy HH:mm");
}

// Servidores Delphi publicados pelo PAServer. São as mesmas chaves do
// services.json no servidor de deploy. IA e Deploy Automático ficam de fora:
// são node e sobem pelo PM2, no fluxo do webhook do GitHub.
const SERVIDORES_PASERVER = [
	"server_linux",
	"server_asaas",
	"server_billing",
	"server_email",
	"server_cobranca",
];

const ACOES = [
	{ id: "parar", label: "Parar", classe: "bg-rose-600 hover:bg-rose-700" },
	{ id: "iniciar", label: "Iniciar", classe: "bg-emerald-600 hover:bg-emerald-700" },
	{ id: "rollback", label: "Rollback", classe: "bg-slate-600 hover:bg-slate-700" },
];

const TEXTO_ACAO = {
	parar: {
		titulo: "Parar para deploy",
		descricao:
			"Guarda uma cópia do binário atual, para o serviço, confere se não sobrou processo órfão e sobe o PAServer para o deploy pelo Delphi.",
		alerta: "O serviço fica fora do ar até você clicar em Iniciar.",
	},
	iniciar: {
		titulo: "Iniciar após o deploy",
		descricao:
			"Fecha o PAServer, sobe o serviço, confere que subiu um único processo e valida o health check.",
		alerta: "Avisa se o binário não mudou desde a parada — sinal de que o deploy não chegou.",
	},
	rollback: {
		titulo: "Voltar a versão anterior",
		descricao:
			"Restaura o último binário guardado, reinicia o serviço e valida. Usar quando a versão nova subiu com problema.",
		alerta: "O serviço reinicia e fica fora do ar por alguns segundos.",
	},
};

export default function ServiceMonitor() {
	const navigate = useNavigate();
	const [services, setServices] = useState([]);
	const [loading, setLoading] = useState(false);
	const [acaoPendente, setAcaoPendente] = useState(null); // { servico, label, acao }
	// Saída de `paserver.sh status`, vinda do servidor de deploy (3006).
	// Passa a ser preenchido quando a rota /servicos/status existir.
	const [statusServidor, setStatusServidor] = useState(null);
	const [executando, setExecutando] = useState(false);
	const [resultado, setResultado] = useState(null); // { ok, mensagem }

	useEffect(() => {
		const serviceList = [
			{
				name: "MultTenant",
				api: reqServiceMultTenant,
				name_server: "server_linux",
				port: 11126,
				descricao: "Servidor principal aonde todos os clientes acessam",
			},
			{
				name: "Asaas",
				api: reqServiceAssas,
				name_server: "server_asaas",
				port: 11128,
				descricao:
					"Servidor que faz a integração com o portal do asaas para criação de cobranças criar conta e usuários",
			},
			{
				name: "Billing",
				api: reqServiceBilling,
				name_server: "server_billing",
				port: 11134,
				descricao:
					"O billing usa o portal asaas mas é ele usado principalmente para criação de contas recorrentes e cobrança no cartão",
			},
			{
				name: "Email",
				api: reqServiceEmail,
				name_server: "server_email",
				port: 11587,
				descricao: "Servidor para envio de e-mail e recuperação de senha",
			},
			{
				name: "Cobrança / WhatsApp",
				api: reqServiceWhatsApp,
				name_server: "server_cobranca",
				port: 11130,
				descricao: "Ele manda para os clientes as cobranças e mensagens pelo whatsApp",
			},
			{
				name: "Inteligência Artificial",
				api: reqServiceAi,
				name_server: "server-ai",
				port: 3005,
				descricao: "Servidor de IA (Gemini) utilizado no chat do SI Advisor e upload de imagens para o S3",
			},
			{
				name: "Deploy Automático",
				api: reqServiceWebhook,
				name_server: "server-upload",
				port: 3006,
				descricao: "Servidor que escuta os webhooks do GitHub e orquestra o deploy automático de todos os projetos",
			},
		];

		async function checkAll() {
			try {
				setLoading(true);
				const results = await Promise.all(
					serviceList.map(async (svc) => {
						const res = await checkService(svc.api);
						return { ...svc, ...res };
					})
				);
				setServices(results);
			} catch (error) {
				console.log(error.message);
			} finally {
				setLoading(false);
			}
		}

		checkAll();
		const interval = setInterval(checkAll, 30000); // atualiza a cada 10s
		return () => clearInterval(interval);
	}, []);

	async function checkService(reqService) {
		const start = performance.now();
		try {
			const response = await reqService.status(); // usa o wrapper Axios
			const end = performance.now();

			return {
				status: "online",
				latency: Math.round(end - start), // tempo de ida e volta em ms
				data: response.data, // qualquer dado extra do servidor
			};
		} catch (error) {
			const end = performance.now();
			return {
				status: "offline",
				latency: Math.round(end - start),
				error: error.message || "Erro desconhecido",
			};
		}
	}

	function abrirConfirmacao(svc, acao) {
		setResultado(null);
		setAcaoPendente({ servico: svc.name_server, label: svc.name, acao });
		handleOpenModal(IDENT_MODAL_ACAO);
	}

	function fecharConfirmacao() {
		handleCloseModal(IDENT_MODAL_ACAO);
		setAcaoPendente(null);
		setResultado(null);
	}

	async function confirmar() {
		setExecutando(true);
		try {
			const response = await reqControlServer.parar(acaoPendente.servico);
			setResultado({ ok: true, mensagem: response.data.mensagem });
		} catch (error) {
			// O interceptor do apisServer joga o corpo da resposta em error.message.
			const corpo = error?.message;
			setResultado({
				ok: false,
				mensagem: corpo?.mensagem || (typeof corpo === "string" ? corpo : "Falha ao chamar o servidor."),
			});
		} finally {
			setExecutando(false);
		}
	}

	const paserver = statusServidor?.paserver;

	return (
		<div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
			<div className="mx-auto max-w-6xl p-4">
				<HeaderReport title="Servidores disponíveis" subTitle="Status dos servidores" />

				{/* PAServer é um só para os cinco Delphi: aberto, ele dá escrita
				    direta nos binários de produção, então o normal é estar parado. */}
				{paserver && (
				<div
					className={`mt-4 flex items-center justify-between rounded-xl border px-4 py-3 ${
						paserver.rodando ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white/80"
					}`}
				>
					<div className="flex items-center gap-2">
						<span
							className={`h-2.5 w-2.5 rounded-full ${
								paserver.rodando ? "bg-amber-500 animate-pulse" : "bg-slate-300"
							}`}
							aria-hidden
						/>
						<span className="text-sm font-medium text-slate-800">PAServer</span>
						<span className="text-sm text-slate-500">
							{paserver.rodando
								? `aberto para deploy — ${paserver.instancias} instância(s), PID ${paserver.pids.trim()}`
								: "fechado"}
						</span>
					</div>

					{paserver.instancias > 1 && (
						<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700">
							mais de uma instância
						</span>
					)}
				</div>
				)}

				{loading && services.length === 0 ? (
					<div className="py-10">
						<Loading />
					</div>
				) : (
					<div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-4">
						{services.map((svc, i) => {
							const online = svc.status === "online";
							const barWidth = `${Math.min(
								100,
								Math.max(6, (2000 - Math.min(svc.latency ?? 0, 2000)) / 20)
							)}%`;

							const temPaserver = SERVIDORES_PASERVER.includes(svc.name_server);

							// Casa pelo name_server, que é a mesma chave do services.json.
							// Sem a rota /servicos/status isto é sempre undefined: os botões
							// aparecem mesmo assim, só os detalhes do processo é que faltam.
							const controle = statusServidor?.servicos?.find(
								(s) => s.servico === svc.name_server
							);

							return (
								<div
									key={i}
									className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white/80 shadow-sm hover:shadow-md transition-shadow"
								>
									{/* filete superior de status */}
									<div
										className={`absolute inset-x-0 top-0 h-1 ${
											online ? "bg-emerald-500" : "bg-rose-500"
										}`}
									/>

									<div className="p-5 space-y-4">
										{/* título + selo de status */}
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<span
													className={`h-2.5 w-2.5 rounded-full ${
														online ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
													}`}
													aria-hidden
												/>
												<h3 className="text-slate-900 font-semibold">{svc.name}</h3>
											</div>

											<span
												className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium
                      ${online ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
											>
												{svc.status}
											</span>
										</div>

										{/* descrição */}
										<p className="text-sm leading-relaxed text-slate-600">{svc.descricao}</p>

										{/* chips informativos */}
										<div className="flex flex-wrap gap-2">
											<span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
												<span className="opacity-70">Nome:</span>
												<strong className="font-medium">{svc.name_server}</strong>
											</span>

											<span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
												<span className="opacity-70">Porta:</span>
												<strong className="font-medium">{svc.port}</strong>
											</span>

											<span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
												<span className="opacity-70">Latência:</span>
												<strong className="font-medium">{svc.latency} ms</strong>
											</span>
										</div>

										{/* barra “indicadora” de latência */}
										<div>
											<div className="h-2 w-full rounded-full bg-slate-100">
												<div
													className={`h-2 rounded-full transition-all ${
														online ? "bg-emerald-500" : "bg-rose-500"
													}`}
													style={{ width: barWidth }}
													aria-label="Indicador de latência"
												/>
											</div>
											<div className="mt-1 text-[10px] text-slate-500">
												Barra maior = menor latência
											</div>
										</div>

										{/* erro (quando offline) */}
										{svc.error && (
											<div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2">
												{svc.error}
											</div>
										)}

										{/* Deploy pelo PAServer: só os cinco Delphi. Bloco comentado
										    abaixo até a rota de parar ter autenticação - hoje quem
										    souber a URL derruba o serviço com um POST. */}
										{/*
										{temPaserver && (
											<div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-3">
												<div className="flex items-center justify-between">
													<span className="text-xs font-medium uppercase tracking-wide text-slate-500">
														Deploy pelo PAServer
													</span>
													{controle?.em_deploy && (
														<span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
															em deploy
														</span>
													)}
												</div>

												{controle ? (
													<>
														<div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600">
															<div>
																<span className="opacity-70">PID:</span>{" "}
																<strong className="font-medium text-slate-800">
																	{controle.main_pid || "-"}
																</strong>
															</div>
															<div>
																<span className="opacity-70">Processos:</span>{" "}
																<strong
																	className={`font-medium ${
																		controle.duplicado
																			? "text-rose-600"
																			: "text-slate-800"
																	}`}
																>
																	{controle.processos}
																</strong>
															</div>
															<div className="col-span-2">
																<span className="opacity-70">Binário:</span>{" "}
																<strong className="font-medium text-slate-800">
																	{formatarData(controle.bin_mtime)}
																</strong>{" "}
																<span className="text-slate-400">
																	({formatarTamanho(controle.bin_size)})
																</span>
															</div>
														</div>

														{controle.duplicado && (
															<div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] leading-snug text-rose-700">
																Mais de um processo deste binário ativo — provável
																instância órfã de um deploy anterior.
															</div>
														)}

														{!controle.sudo_ok && (
															<div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] leading-snug text-amber-800">
																Sem permissão de sudo para systemctl: as ações abaixo
																vão falhar até o sudoers ser configurado.
															</div>
														)}
													</>
												) : (
													<p className="text-[11px] leading-snug text-slate-500">
														PID, processos e data do binário aparecem quando a rota de
														status do servidor de deploy existir.
													</p>
												)}

												<div className="flex flex-col gap-2 pt-0.5">
													{ACOES.map((acao) => (
														<button
															key={acao.id}
															type="button"
															onClick={() => abrirConfirmacao(svc, acao.id)}
															className={`w-full rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors ${acao.classe}`}
														>
															{acao.label}
														</button>
													))}
												</div>
											</div>
										)}
										*/}
									</div>
								</div>
							);
						})}
					</div>
				)}

				<div className="mt-6">
					<BtnBack onClick={() => navigate("/")} />
				</div>
			</div>

			<ModalMain ident={IDENT_MODAL_ACAO} onClose={() => setAcaoPendente(null)}>
				{acaoPendente && (
					<div className="max-w-md space-y-4 p-1">
						<h2 className="text-lg font-semibold text-slate-900">
							{TEXTO_ACAO[acaoPendente.acao].titulo}
						</h2>

						<div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
							<strong className="font-medium">{acaoPendente.label}</strong>{" "}
							<span className="text-slate-500">({acaoPendente.servico})</span>
						</div>

						<p className="text-sm leading-relaxed text-slate-600">
							{TEXTO_ACAO[acaoPendente.acao].descricao}
						</p>

						<div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs leading-snug text-amber-800">
							{TEXTO_ACAO[acaoPendente.acao].alerta}
						</div>

						{/* Só o Parar tem rota no servidor de deploy até agora. */}
						{acaoPendente.acao !== "parar" && (
							<div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-500">
								Ainda sem rota no servidor — por enquanto só o Parar funciona.
							</div>
						)}

						{resultado && (
							<div
								className={`rounded-md border p-2.5 text-xs whitespace-pre-wrap font-mono leading-relaxed ${
									resultado.ok
										? "border-emerald-200 bg-emerald-50 text-emerald-800"
										: "border-rose-200 bg-rose-50 text-rose-800"
								}`}
							>
								{resultado.mensagem}
							</div>
						)}

						<div className="flex justify-end gap-2 pt-1">
							<button
								type="button"
								onClick={fecharConfirmacao}
								className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
							>
								{resultado ? "Fechar" : "Cancelar"}
							</button>
							{!resultado && (
								<button
									type="button"
									disabled={acaoPendente.acao !== "parar" || executando}
									onClick={confirmar}
									className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
										acaoPendente.acao !== "parar" || executando
											? "bg-slate-400 opacity-60 cursor-not-allowed"
											: "bg-rose-600 hover:bg-rose-700"
									}`}
								>
									{executando ? "Executando..." : "Confirmar"}
								</button>
							)}
						</div>
					</div>
				)}
			</ModalMain>
		</div>
	);

	// return (
	// 	<div className="p-4">
	// 		<HeaderReport title="Servidores disponíveis" subTitle={"Status dos servidores"} />
	// 		<div className="grid gap-4 grid-cols-1 md:grid-cols-3 p-4">
	// 			{loading ? (
	// 				<Loading />
	// 			) : (
	// 				<>
	// 					{services.map((svc, i) => (
	// 						<div key={i} className="p-4 rounded-lg border shadow-sm bg-white dark:bg-gray-900">
	// 							<h3 className="font-semibold">{`${svc.name}`}</h3>
	// 							<h3 className="">{`Nome: ${svc.name_server}`}</h3>
	// 							<p>Porta: {svc.port}</p>
	// 							<p>
	// 								Status:{" "}
	// 								<span className={svc.status === "online" ? "text-green-500" : "text-red-500"}>
	// 									{svc.status}
	// 								</span>
	// 							</p>
	// 							<p>Latência: {svc.latency} ms</p>
	// 							<p>Descrição: {svc.descricao}</p>
	// 							{svc.error && <p className="text-red-400 text-xs">{svc.error}</p>}
	// 						</div>
	// 					))}
	// 				</>
	// 			)}
	// 		</div>
	// 		<BtnBack
	// 			onClick={() => {
	// 				navigate("/");
	// 			}}
	// 		/>
	// 	</div>
	// );
}
